import { useCallback } from 'react';
import useSWR from 'swr';
import { revalidateChronologyData } from '@/hooks/chronology-cache';
import { backendClient } from '@/lib/api-client';

/** Per-matter cap on user-facing chronologies (excludes the trunk).
 *  Matters seed with 2 default chronologies (Factual Background and
 *  Procedural), so this allows 3 additional custom chronologies.
 *  Mirrored on the server — keep in sync with `CHRONOLOGIES_PER_MATTER_CAP`
 *  in `backend/app/api/endpoints/chronology/chronologies.py`. */
export const CHRONOLOGIES_PER_MATTER_CAP = 5;

/** Cap on documents that feed one custom chronology. Keep in sync with
 * `CUSTOM_CHRONOLOGY_DOCUMENT_CAP` in
 * `backend/app/modules/ingestion/consolidation/chronologies/custom.py`. */
export const CUSTOM_CHRONOLOGY_DOCUMENT_CAP = 2000;

/** Default matter lenses. Configuration is tags-only. */
export const SYSTEM_CHRONOLOGY_TYPES = new Set([
  'factual',
  'factual_background',
  'procedural',
]);

export interface ChronologyTagGroupInfo {
  id: string;
  group_key: string;
  label: string;
  color: string | null;
  is_system: boolean;
  sort_order: number;
}

export interface ChronologyTagInfo {
  id: string;
  group_id: string | null;
  group_key: string | null;
  group_label: string | null;
  group_color: string | null;
  tag_key: string;
  label: string;
  description: string | null;
  is_system: boolean;
  sort_order: number;
}

export interface ChronologyInfo {
  id: string;
  name: string;
  description: string | null;
  chronology_type: string;
  is_custom?: boolean;
  document_scope?: 'include' | 'exclude';
  tag_groups: ChronologyTagGroupInfo[];
  tags: ChronologyTagInfo[];
  created_at: string | null;
}

interface ChronologiesResponse {
  chronologies: ChronologyInfo[];
}

export interface TagGroupInput {
  group_key?: string;
  label: string;
  color?: string | null;
}

export interface TagInput {
  tag_key?: string;
  label: string;
  description?: string;
  group_key: string;
}

export interface CreateChronologyBody {
  name: string;
  description?: string;
  prompt?: string;
  document_guids?: string[];
  tag_groups?: TagGroupInput[];
  tags?: TagInput[];
}

export type PatchChronologyBody = Partial<CreateChronologyBody>;

export function useChronologies(matterGuid: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR<ChronologiesResponse>(
    matterGuid ? `/matters/${matterGuid}/chronologies` : null,
    async (url: string) => {
      const response = await backendClient.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000,
    }
  );

  const createChronology = useCallback(
    async (body: CreateChronologyBody) => {
      if (!matterGuid) throw new Error('matterGuid required');
      const response = await backendClient.post(
        `/matters/${matterGuid}/chronologies`,
        body,
      );
      await mutate();
      return response.data as ChronologyInfo;
    },
    [matterGuid, mutate],
  );

  const patchChronology = useCallback(
    async (chronologyId: string, body: PatchChronologyBody) => {
      if (!matterGuid) throw new Error('matterGuid required');
      const response = await backendClient.patch(
        `/matters/${matterGuid}/chronologies/${chronologyId}`,
        body,
      );
      await Promise.all([
        mutate(),
        revalidateChronologyData(matterGuid, chronologyId),
      ]);
      return response.data as ChronologyInfo;
    },
    [matterGuid, mutate],
  );

  const deleteChronology = useCallback(
    async (chronologyId: string) => {
      if (!matterGuid) throw new Error('matterGuid required');
      await backendClient.delete(`/matters/${matterGuid}/chronologies/${chronologyId}`);
      await mutate();
    },
    [matterGuid, mutate],
  );

  return {
    chronologies: data?.chronologies || [],
    isLoading,
    isError: error,
    mutate,
    createChronology,
    patchChronology,
    deleteChronology,
  };
}
